import * as React from 'react';
import { useResize } from './useResize';
export function useCarousel(params) {
    const refContainer = React.useRef(null);
    const refTrack = React.useRef(null);
    const [carouselState, dispatch] = React.useReducer(carouselReducer, {
        activeIndex: 0,
        maxIndex: 0,
        offsets: [],
        count: 0,
        showNavigation: false,
    });
    useResize({
        callback: initCarousel,
        debounce: 100,
        immediate: true,
        on: 'x',
    });
    React.useEffect(() => {
        initCarousel();
    }, [carouselState.count]);
    return {
        ...carouselState,
        goToIndex,
        goToNext,
        goToPrev,
        reinitialise,
        recalculate: initCarousel,
        offset: carouselState.offsets[carouselState.activeIndex],
        refContainer,
        refTrack,
        count: carouselState.count,
        showNavigation: carouselState.showNavigation,
    };
    /**
     * Calculate the px offset for the track for the target item index.
     * @param index
     * @returns
     */
    function calculateOffset(index) {
        if (!refTrack.current) {
            return 0;
        }
        const items = Array.from(refTrack.current.children);
        const itemsUntilIndex = items.slice(0, index);
        const sum = itemsUntilIndex
            .map((el) => el.getBoundingClientRect().width)
            .reduce((p, c) => p + c, 0);
        return sum;
    }
    /**
     * Initialise the reducer state.
     */
    function initCarousel() {
        const offsets = [];
        // Pre-calculate the px offset for each item in the carousel.
        Array.from(Array(params.count)).forEach((_, index) => {
            offsets.push(calculateOffset(index));
        });
        // Determine what the max index value needs to be in order for the last item to be within view.
        const containerWidth = refContainer.current?.getBoundingClientRect().width ?? 0;
        const trackWidth = offsets[offsets.length - 1];
        let maxIndex = 0;
        for (let index = 0; index < offsets.length; index++) {
            const offset = offsets[index];
            const remainder = trackWidth - offset;
            if (remainder <= containerWidth) {
                maxIndex = index + 1;
                break;
            }
        }
        // Initialise the carousel state.
        dispatch({
            type: 'init',
            payload: {
                offsets,
                maxIndex,
                count: params.count,
                showNavigation: containerWidth <= trackWidth,
            },
        });
    }
    /**
     * Go to the next item.
     */
    function goToNext(wrap) {
        dispatch({ type: 'next', payload: { wrap: wrap ?? false } });
    }
    /**
     * Go to the previous item.
     */
    function goToPrev(wrap) {
        dispatch({ type: 'prev', payload: { wrap: wrap ?? false } });
    }
    /**
     * Go to the target index.
     */
    function goToIndex(index) {
        dispatch({ type: 'goto', payload: index });
    }
    /**
     * Go to the target index.
     */
    function reinitialise(count) {
        dispatch({ type: 'reinitialise', payload: count });
    }
    function carouselReducer(state, action) {
        switch (action.type) {
            case 'init':
                return {
                    activeIndex: 0,
                    maxIndex: action.payload.maxIndex,
                    offsets: action.payload.offsets,
                    count: action.payload.count,
                    showNavigation: action.payload.showNavigation,
                };
            case 'reinitialise':
                return {
                    ...state,
                    count: action.payload,
                };
            case 'goto':
                return { ...state, activeIndex: clamp(action.payload) };
            case 'next':
                if (action.payload.wrap && state.activeIndex === state.maxIndex) {
                    return { ...state, activeIndex: 0 };
                }
                return { ...state, activeIndex: clamp(state.activeIndex + 1) };
            case 'prev':
                if (action.payload.wrap && state.activeIndex === 0) {
                    return { ...state, activeIndex: state.maxIndex };
                }
                return { ...state, activeIndex: clamp(state.activeIndex - 1) };
        }
        /** Clamp index to be within the min/max index range. */
        function clamp(value) {
            return Math.max(Math.min(value, state.maxIndex), 0);
        }
    }
}
